import { moment } from '@hotjar/insights-common/lib/moment';
import { last, first } from 'lodash-es';
import type { RadioDateFilterValueType } from '../../../../components/StatelessComponent/SingleFilter/GenericBloc/GenericRadioDateFilter/GenericRadioDateType';
import { RadioDateFilterOperation } from '../../../../components/StatelessComponent/SingleFilter/GenericBloc/GenericRadioDateFilter/GenericRadioDateType';

import type { IntermediateFormat } from '../../../../domains/global-filters/model/IntermediateFormat';
import type {
  QueryFormatFilter,
  QueryKeyEnum,
} from '../../../../domains/global-filters/model/QueryFormat';
import { QueryMapOperandEnum } from '../../../../domains/global-filters/model/QueryFormat';

const shouldNotHappen = (never: never): void => {
  throw new Error(`Unknown type: ${never}`);
};

export function fromUiToQueryFormat(
  uiValue: RadioDateFilterValueType,
  queryKey: QueryKeyEnum,
): QueryFormatFilter {
  if (!uiValue) {
    return {};
  }
  switch (uiValue.operation) {
    case RadioDateFilterOperation.IS_EXACTLY_DAYS_AGO: {
      return {
        [QueryMapOperandEnum.DAYS_AGO]: {
          [queryKey]: +uiValue.value,
        },
      };
    }
    case RadioDateFilterOperation.IS_MORE_THAN_DAYS_AGO: {
      return {
        [QueryMapOperandEnum.DAYS_AGO]: {
          [QueryMapOperandEnum.GREATER_THAN]: {
            [queryKey]: +uiValue.value,
          },
        },
      };
    }
    case RadioDateFilterOperation.IS_LESS_THAN_DAYS_AGO: {
      return {
        [QueryMapOperandEnum.DAYS_AGO]: {
          [QueryMapOperandEnum.LESS_THAN]: {
            [queryKey]: +uiValue.value,
          },
        },
      };
    }
    case RadioDateFilterOperation.IS_ON: {
      // For some weird reason, the date follow the RANGE_IN format
      return {
        [QueryMapOperandEnum.RANGE_IN]: {
          [queryKey]: [
            moment(uiValue.value).startOf('day').unix(),
            moment(uiValue.value).endOf('day').unix(),
          ],
        },
      };
    }
    case RadioDateFilterOperation.IS_AFTER: {
      const date = moment(uiValue.value).endOf('day');
      return {
        [QueryMapOperandEnum.GREATER_THAN]: {
          [queryKey]: date.toISOString(),
        },
      };
    }
    case RadioDateFilterOperation.IS_BEFORE: {
      const date = moment(uiValue.value).startOf('day');
      return {
        [QueryMapOperandEnum.LESS_THAN]: {
          [queryKey]: date.toISOString(),
        },
      };
    }
    case RadioDateFilterOperation.IS_UNKNOWN: {
      return {
        [QueryMapOperandEnum.UNKNOWN]: {
          [queryKey]: true,
        },
      };
    }
    case RadioDateFilterOperation.HAS_ANY_VALUE: {
      return {
        [QueryMapOperandEnum.NOT]: {
          [QueryMapOperandEnum.UNKNOWN]: {
            [queryKey]: true,
          },
        },
      };
    }
    default: {
      shouldNotHappen(uiValue);
    }
  }

  throw new Error(`Operation not implemented: ${uiValue}`);
}

export function fromIntermediateToUiFormat(
  intermediateValue: IntermediateFormat,
): RadioDateFilterValueType {
  const { operation, negate, value } = intermediateValue;
  const lastOperation = last(operation);
  if (!lastOperation) {
    throw new Error(`Operation not implemented: ${lastOperation}`);
  }
  switch (lastOperation) {
    case QueryMapOperandEnum.CONTAINS:
    case QueryMapOperandEnum.STARTS_WITH:
    case QueryMapOperandEnum.ENDS_WITH:
    case QueryMapOperandEnum.NOT:
    case QueryMapOperandEnum.REGEX:
    case QueryMapOperandEnum.SIMPLE_MATCH:
    case QueryMapOperandEnum.EQUAL:
    case QueryMapOperandEnum.DROPOFF: {
      // We ignore
      break;
    }
    case QueryMapOperandEnum.UNKNOWN: {
      return negate === true
        ? {
            operation: RadioDateFilterOperation.HAS_ANY_VALUE,
          }
        : {
            operation: RadioDateFilterOperation.IS_UNKNOWN,
          };
    }
    case QueryMapOperandEnum.DAYS_AGO: {
      return {
        operation: RadioDateFilterOperation.IS_EXACTLY_DAYS_AGO,
        value: +value[0],
      };
    }
    case QueryMapOperandEnum.RANGE_IN: {
      return {
        operation: RadioDateFilterOperation.IS_ON,
        // The format is in seconds
        value: new Date(+value[0] * 1000),
      };
    }
    case QueryMapOperandEnum.GREATER_THAN: {
      const firstOperation = first(operation);
      if (firstOperation === QueryMapOperandEnum.DAYS_AGO) {
        return {
          operation: RadioDateFilterOperation.IS_MORE_THAN_DAYS_AGO,
          value: +value[0],
        };
      }
      return {
        operation: RadioDateFilterOperation.IS_AFTER,
        // The format is an ISO Date string
        value: new Date(value[0] as string),
      };
    }
    case QueryMapOperandEnum.LESS_THAN: {
      const firstOperation = first(operation);
      if (firstOperation === QueryMapOperandEnum.DAYS_AGO) {
        return {
          operation: RadioDateFilterOperation.IS_LESS_THAN_DAYS_AGO,
          value: +value[0],
        };
      }
      return {
        operation: RadioDateFilterOperation.IS_BEFORE,
        // The format is an ISO Date string
        value: new Date(value[0] as string),
      };
    }
    default: {
      shouldNotHappen(lastOperation);
    }
  }
  throw new Error(`Operation not implemented: ${lastOperation}`);
}
